/**
 * Wraps an info tooltip and adds:
 * - Hover / focus → show popover (desktop)
 * - Long press → toggle popover (touch)
 *
 * Click/tap still works via native popovertarget when JS isn't needed.
 *
 * Note: do not use a public `popover` property on this element — it clashes with
 * the built-in HTMLElement.popover attribute and produces invalid values.
 */
const SHOW_DELAY_MS = 100;
const HIDE_DELAY_MS = 100;
const LONG_PRESS_MS = 400;
const MOVE_THRESHOLD_PX = 10;

class InfoTooltip extends HTMLElement {
  #content = null;
  #trigger = null;
  #showTimer = null;
  #hideTimer = null;
  #longPressTimer = null;
  #longPressFired = false;
  #touchStart = null;

  #onMouseEnter = () => this.#scheduleShow();
  #onMouseLeave = () => this.#scheduleHide();
  #onFocus = () => this.#scheduleShow();
  #onBlur = () => this.#scheduleHide();
  #onTouchStart = (event) => this.#handleTouchStart(event);
  #onTouchMove = (event) => this.#handleTouchMove(event);
  #onTouchEnd = () => this.#handleTouchEnd();
  #onClick = (event) => this.#handleClick(event);
  #onContextMenu = (event) => this.#handleContextMenu(event);
  #onSelectStart = (event) => event.preventDefault();

  connectedCallback() {
    this.#trigger = this.querySelector('.info-tooltip__trigger');
    this.#content = this.querySelector('.info-tooltip__content');

    if (!this.#trigger || !this.#content || typeof this.#content.showPopover !== 'function') {
      return;
    }

    this.#trigger.addEventListener('mouseenter', this.#onMouseEnter);
    this.#trigger.addEventListener('mouseleave', this.#onMouseLeave);
    this.#trigger.addEventListener('focus', this.#onFocus);
    this.#trigger.addEventListener('blur', this.#onBlur);
    this.#trigger.addEventListener('touchstart', this.#onTouchStart, { passive: true });
    this.#trigger.addEventListener('touchmove', this.#onTouchMove, { passive: true });
    this.#trigger.addEventListener('touchend', this.#onTouchEnd);
    this.#trigger.addEventListener('touchcancel', this.#onTouchEnd);
    this.#trigger.addEventListener('click', this.#onClick);
    this.#trigger.addEventListener('contextmenu', this.#onContextMenu);
    this.#trigger.addEventListener('selectstart', this.#onSelectStart);
  }

  disconnectedCallback() {
    this.#clearTimers();
    if (!this.#trigger) return;

    this.#trigger.removeEventListener('mouseenter', this.#onMouseEnter);
    this.#trigger.removeEventListener('mouseleave', this.#onMouseLeave);
    this.#trigger.removeEventListener('focus', this.#onFocus);
    this.#trigger.removeEventListener('blur', this.#onBlur);
    this.#trigger.removeEventListener('touchstart', this.#onTouchStart);
    this.#trigger.removeEventListener('touchmove', this.#onTouchMove);
    this.#trigger.removeEventListener('touchend', this.#onTouchEnd);
    this.#trigger.removeEventListener('touchcancel', this.#onTouchEnd);
    this.#trigger.removeEventListener('click', this.#onClick);
    this.#trigger.removeEventListener('contextmenu', this.#onContextMenu);
    this.#trigger.removeEventListener('selectstart', this.#onSelectStart);
  }

  #isOpen() {
    return this.#content.matches(':popover-open');
  }

  #show() {
    if (this.#isOpen()) return;
    this.#content.showPopover();
  }

  #hide() {
    if (!this.#isOpen()) return;
    this.#content.hidePopover();
  }

  #toggle() {
    if (this.#isOpen()) {
      this.#hide();
    } else {
      this.#show();
    }
  }

  #clearTimers() {
    clearTimeout(this.#showTimer);
    clearTimeout(this.#hideTimer);
    clearTimeout(this.#longPressTimer);
    this.#showTimer = null;
    this.#hideTimer = null;
    this.#longPressTimer = null;
  }

  #scheduleShow() {
    this.#clearTimers();
    this.#showTimer = setTimeout(() => this.#show(), SHOW_DELAY_MS);
  }

  #scheduleHide() {
    this.#clearTimers();
    this.#hideTimer = setTimeout(() => this.#hide(), HIDE_DELAY_MS);
  }

  #handleTouchStart(event) {
    const touch = event.touches[0];
    if (!touch) return;

    this.#longPressFired = false;
    this.#touchStart = { x: touch.clientX, y: touch.clientY };
    clearTimeout(this.#longPressTimer);
    this.#longPressTimer = setTimeout(() => {
      this.#longPressFired = true;
      this.#clearTextSelection();
      this.#toggle();
    }, LONG_PRESS_MS);
  }

  #handleTouchMove(event) {
    if (!this.#touchStart || !this.#longPressTimer) return;
    const touch = event.touches[0];
    if (!touch) return;

    const dx = Math.abs(touch.clientX - this.#touchStart.x);
    const dy = Math.abs(touch.clientY - this.#touchStart.y);
    if (dx > MOVE_THRESHOLD_PX || dy > MOVE_THRESHOLD_PX) {
      clearTimeout(this.#longPressTimer);
      this.#longPressTimer = null;
    }
  }

  #handleTouchEnd() {
    clearTimeout(this.#longPressTimer);
    this.#longPressTimer = null;
    this.#touchStart = null;
  }

  #handleClick(event) {
    // Long press already toggled; prevent the native popovertarget click from flipping it back
    if (this.#longPressFired) {
      event.preventDefault();
      event.stopPropagation();
      this.#longPressFired = false;
    }
  }

  #handleContextMenu(event) {
    // Long-press selection / callout on touch devices
    event.preventDefault();
    this.#clearTextSelection();
  }

  #clearTextSelection() {
    const selection = window.getSelection?.();
    if (selection && selection.rangeCount > 0) {
      selection.removeAllRanges();
    }
  }
}

customElements.define('info-tooltip', InfoTooltip);
